'use client';

import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import EditableElement from "@/components/customize/EditableElement";
import Image from "next/image";

const getDefaultValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

export default function OpenInvitation({
  onOpen,
  props,
  isCustomize = true,
  title,
  subtitle,
  buttonText,
  backgroundImage,
  date,
  dateFormat = "long",
  templateMeta,
  __isEditable = false,
  __sectionId = "openInvitationConfig",
  __onFieldEdit,
  __onFieldChange,
  __sectionSchema,
  __onFileSelected,
  __focusFieldName,
  __isMobile,
}) {
  const [show, setShow] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const dateTextClass = __isMobile ? "text-base" : "text-base md:text-lg";

  const config = useMemo(() => {
    if (props) return props;
    return {
      title,
      subtitle,
      buttonText,
      backgroundImage,
      date,
    };
  }, [props, title, subtitle, buttonText, backgroundImage, date]);

  const getSchemaField = (fieldName) =>
    __sectionSchema?.fields?.find((field) => field.name === fieldName);

  const getSchemaDefault = (fieldName) =>
    getSchemaField(fieldName)?.defaultValue || getSchemaField(fieldName)?.placeholder || "";

  const safeBackground = getDefaultValue(
    config?.backgroundImage,
    getSchemaDefault("backgroundImage"),
    config?.image,
    props?.image
  );
  const safeTitle = getDefaultValue(
    config?.title,
    getSchemaDefault("title"),
    config?.heading
  );
  const safeSubtitle = getDefaultValue(
    config?.subtitle,
    getSchemaDefault("subtitle"),
    config?.description
  );
  const safeButtonText = getDefaultValue(
    config?.buttonText,
    getSchemaDefault("buttonText"),
    buttonText
  );
  
  const formatDateDisplay = (dateString, formatType) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;

    const dayNameEn = d.toLocaleDateString("en-US", { weekday: "long" });
    const monthLongEn = d.toLocaleDateString("en-US", { month: "long" });
    const monthShortEn = d.toLocaleDateString("en-US", { month: "short" });

    const dayNameId = d.toLocaleDateString("id-ID", { weekday: "long" });
    const monthLongId = d.toLocaleDateString("id-ID", { month: "long" });
    const monthShortId = d.toLocaleDateString("id-ID", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();

    const getSuffix = (n) => {
      if (n >= 11 && n <= 13) return "th";
      switch (n % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };

    switch (formatType) {
      case "short":
        return `${monthShortEn} ${day}, ${year}`;
      case "numeric":
        return `${String(day).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${year}`;
      case "full":
        return `${dayNameEn}, ${monthLongEn} ${day}${getSuffix(day)}, ${year}`;
      case "id-long":
        return `${dayNameId}, ${day} ${monthLongId} ${year}`;
      case "id-short":
        return `${day} ${monthShortId} ${year}`;
      case "long":
      default:
        return `${dayNameEn}, ${day} ${monthLongEn} ${year}`;
    }
  };

  const safeDate = formatDateDisplay(getDefaultValue(
    templateMeta?.marriageContractDate,
    templateMeta?.receptionDate,
    config?.date,
    props?.date
  ), config?.dateFormat || dateFormat);

  const handleOpen = () => {
    if (isCustomize || __isEditable) return;
    setIsClosing(true);
    setTimeout(() => {
      setShow(false);
      onOpen && onOpen();
    }, 500);
  };

  const handleEdit = (fieldName, editType = "text") => {
    if (__onFieldEdit) {
      __onFieldEdit(__sectionId, fieldName, editType);
    }
  };

  const handleChange = (fieldName, newValue) => {
    if (__onFieldChange) {
      __onFieldChange(__sectionId, fieldName, newValue);
    }
  };

  const handleBackgroundFileSelected = (file, blobUrl) => {
    if (blobUrl) {
      handleChange("backgroundImage", blobUrl);
    }
    if (__onFileSelected) {
      __onFileSelected(`openInvitationConfig.props.backgroundImage`, file, blobUrl);
    }
  };

  const isFieldActive = (fieldName) => __focusFieldName === fieldName;

  return (
    <AnimatePresence>
      {show && (
        <div className="absolute inset-0 z-999 bg-black">
          <EditableElement
            type="background"
            value={safeBackground}
            onChange={(val) => handleChange("backgroundImage", val)}
            isEditable={__isEditable}
            fieldName="backgroundImage"
            schemaFieldPath="backgroundImage"
            onEditStart={() => handleEdit("backgroundImage", "image")}
            onFileSelected={handleBackgroundFileSelected}
            className="absolute inset-0 w-full h-full"
            isMobile={__isMobile}
          >
            {safeBackground && (
              <Image
                src={safeBackground}
                alt="Background"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
                priority
                unoptimized
              />
            )}
            <div className="absolute inset-0 bg-linear-to-b from-black/35 to-black/60" />
          </EditableElement>

          <div
            className={`absolute inset-0 z-10 flex flex-col items-center justify-between px-6 py-10 text-white pointer-events-none transition-opacity duration-700 will-change-[opacity] ${isClosing ? 'opacity-0' : 'opacity-100'}`}
          >
            <div className="text-center mt-5 space-y-2 pointer-events-auto">
              {safeSubtitle && (
                <EditableElement
                  type="text"
                  value={safeSubtitle}
                  onChange={(val) => handleChange("subtitle", val)}
                  isEditable={__isEditable}
                  fieldName="subtitle"
                  schemaFieldPath="subtitle"
                  onEditStart={() => handleEdit("subtitle")}
                  useContentEditable={true}
                  isActive={isFieldActive("subtitle")}
                  isMobile={__isMobile}
                >
                  <p className="text-sm font-light font-sans">{safeSubtitle}</p>
                </EditableElement>
              )}
              {safeTitle && (
                <EditableElement
                  type="text"
                  value={safeTitle}
                  onChange={(val) => handleChange("title", val)}
                  isEditable={__isEditable}
                  fieldName="title"
                  schemaFieldPath="title"
                  onEditStart={() => handleEdit("title")}
                  useContentEditable={true}
                  isActive={isFieldActive("title")}
                  isMobile={__isMobile}
                >
                  <h1 className="text-4xl font-cormorant font-bold uppercase tracking-wide leading-tight whitespace-pre-line">
                    {String(safeTitle).replace(" & ", "\n&\n")}
                  </h1>
                </EditableElement>
              )}
            </div>

            <div className="mb-5 text-center w-full space-y-2 pointer-events-auto">
              {safeDate && (
                <div className="block w-full">
                  <EditableElement
                    type="text"
                    value={safeDate}
                    isEditable={__isEditable}
                    fieldName="openInvitationDate"
                    onEditStart={() => __onFieldEdit && __onFieldEdit("general", "marriageContractDate")}
                    useContentEditable={false}
                  >
                    <p className={`font-light font-sans ${dateTextClass}`}>{safeDate}</p>
                  </EditableElement>
                </div>
              )}
              <div className="block w-full mt-3">
                <EditableElement
                  type="text"
                  value={safeButtonText}
                  onChange={(val) => handleChange("buttonText", val)}
                  isEditable={__isEditable}
                  fieldName="buttonText"
                  schemaFieldPath="buttonText"
                  onEditStart={() => handleEdit("buttonText")}
                  useContentEditable={true}
                  isActive={isFieldActive("buttonText")}
                  isMobile={__isMobile}
                >
                  <button
                    onClick={handleOpen}
                    className="rounded-full bg-white/80 px-10 py-3 text-black text-sm font-medium transition hover:bg-white cursor-pointer"
                  >
                    {safeButtonText}
                  </button>
                </EditableElement>
              </div>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
